import React, { useMemo } from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Legend,
	ResponsiveContainer,
	Tooltip,
	TooltipProps,
	XAxis,
	YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { IncentiveComparisonPoint } from "../../services/dashboard.service";
import { PROGRAM_DAYS } from "../../constants/program";

const EARNED_COLOR = "#1D9E75";
const POTENTIAL_COLOR = "#7F77DD";

interface EarnedVsPotentialChartProps {
	data: IncentiveComparisonPoint[] | undefined;
	isLoading: boolean;
}

const gbpFormatter = new Intl.NumberFormat("en-GB", {
	style: "currency",
	currency: "GBP",
});

const buildSeries = (
	data: IncentiveComparisonPoint[] | undefined,
): IncentiveComparisonPoint[] => {
	const byDay = new Map<number, IncentiveComparisonPoint>();
	(data ?? []).forEach((p) => byDay.set(p.day, p));
	return Array.from({ length: PROGRAM_DAYS }, (_, i) => {
		const day = i + 1;
		const existing = byDay.get(day);
		return existing ?? { day, earned: 0, potential: 0 };
	});
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = ({
	active,
	payload,
	label,
}) => {
	if (!active || !payload || payload.length === 0) return null;

	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 1,
				px: 1.5,
				py: 1,
				boxShadow: 2,
			}}
		>
			{payload.map((entry) => (
				<Typography key={entry.dataKey as string} variant="body2">
					{entry.name}: Day {label} —{" "}
					{gbpFormatter.format(Number(entry.value ?? 0))}
				</Typography>
			))}
		</Box>
	);
};

const EarnedVsPotentialChart: React.FC<EarnedVsPotentialChartProps> = ({
	data,
	isLoading,
}) => {
	const { t } = useTranslation("dashboard");

	const series = useMemo(() => buildSeries(data), [data]);
	const hasAnyData = series.some((p) => p.earned > 0 || p.potential > 0);

	return (
		<Box
			sx={{
				bgcolor: "background.paper",
				borderRadius: 2,
				boxShadow: 1,
				p: 3,
				mt: 3,
			}}
		>
			<Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
				{t("earnedVsPotentialIncentives")}
			</Typography>

			{isLoading ? (
				<Skeleton variant="rectangular" height={280} />
			) : !hasAnyData ? (
				<Box
					sx={{
						height: 280,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					<Typography variant="body2" color="textSecondary">
						{t("noIncentiveDataYet")}
					</Typography>
				</Box>
			) : (
				<ResponsiveContainer width="100%" height={280}>
					<BarChart
						data={series}
						margin={{ top: 16, right: 24, bottom: 0, left: 0 }}
					>
						<CartesianGrid strokeDasharray="3 3" vertical={false} />
						<XAxis dataKey="day" allowDecimals={false} />
						<YAxis
							allowDecimals={true}
							tickFormatter={(v: number) => gbpFormatter.format(v)}
						/>
						<Tooltip content={<CustomTooltip />} />
						<Legend />
						<Bar
							dataKey="earned"
							name={t("earned") as string}
							fill={EARNED_COLOR}
							isAnimationActive={false}
						/>
						<Bar
							dataKey="potential"
							name={t("potential") as string}
							fill={POTENTIAL_COLOR}
							isAnimationActive={false}
						/>
					</BarChart>
				</ResponsiveContainer>
			)}
		</Box>
	);
};

export default EarnedVsPotentialChart;
